import { LinkId } from '../../../common/AnalyticsContext/LinkId';
import { applyTrackingId } from '../../../common/AnalyticsContext/applyTrackingId';
import { useSearchFilters } from '../../../common/components/SearchHeader/filters/useSearchFilters';
import { FreeTextSearch } from '../../../common/schema/hannibal/types.generated';
import { useMobile } from '../../../common/services/screenSize/ScreenSizeService';
import { CovidSpecialtyMessage } from '../../CovidSpecialtyMessage/CovidSpecialtyMessage';
import { specialtyIsCovax } from '../../CovidSpecialtyMessage/specialtyIsCovax';
import { ExtendedSearch } from '../../ExtendedSearch/ExtendedSearch';
import { useNoFilter, useResetFilters } from '../../FilterContainer/filters/useResetFilters';
import { SearchResultAd, SearchResultItem } from '../../types';
import { ProfileCard } from '../ResultList/ProfileCards/ProfileCard';
import { Sidebar } from '../Sidebar/Sidebar';
import { CardsContainer, SidebarContainer } from '../common.styles';
import {
  AdContainer,
  HintHeader,
  Hint,
  HintListItemLink,
  Message,
  NoResultsContainer,
  NoResultsView,
  Sign,
  StyledImage,
  GpAdsContainer,
} from './NoResults.styles';
import { SeoFallback } from './SeoFallback';

type Props = {
  loading?: boolean;
  ad?: SearchResultAd;
  items?: SearchResultItem[];
  showingExtended?: boolean;
  extendedResults?: FreeTextSearch;
  extendedFetchMore?: VoidFunction;
  extendedFetchingMore?: boolean;
};

export const NoResults = ({
  loading = false,
  ad,
  items = [],
  showingExtended,
  extendedResults,
  extendedFetchMore,
  extendedFetchingMore,
}: Props) => {
  const mobile = useMobile();
  const handleClick = useResetFilters();
  const noFilter = useNoFilter();
  const [filters] = useSearchFilters();

  const isCovidSearch = specialtyIsCovax(filters.topic?.specialist);

  return (
    <NoResultsContainer>
      <CardsContainer>
        <NoResultsView isLoading={loading}>
          {isCovidSearch && <CovidSpecialtyMessage />}
          <Sign>
            <StyledImage />
            <Message>
              <HintHeader>No results found</HintHeader>
              <Hint>
                We may add appointments to this page later. Please use different search keywords
                {noFilter && `.`}
                {!noFilter && (
                  <>
                    {' '}
                    or{' '}
                    <HintListItemLink
                      onClick={handleClick}
                      {...applyTrackingId(LinkId.NO_RESULTS_RESET_FILTER)}
                    >
                      reset your filters.
                    </HintListItemLink>
                  </>
                )}
              </Hint>
            </Message>
          </Sign>

          {!showingExtended && (
            <AdContainer>
              {items.map((item, i) => (
                <ProfileCard
                  key={JSON.stringify(item)}
                  card={item}
                  index={i}
                  resultType="standard"
                />
              ))}
              {ad && <ProfileCard card={ad} index={items.length} resultType="standard" />}
            </AdContainer>
          )}

          {showingExtended && extendedFetchMore && extendedResults && (
            <>
              {items
                .filter(item => item.id === 'gpOrTelehealth')
                .map((item, i) => (
                  <GpAdsContainer key={JSON.stringify(item)}>
                    <ProfileCard card={item} index={i} resultType="standard" />
                  </GpAdsContainer>
                ))}
              <ExtendedSearch
                primaryResults={[]}
                fetchMore={extendedFetchMore}
                fetchingMore={extendedFetchingMore ?? false}
                data={extendedResults}
              />
            </>
          )}
          <SeoFallback />
        </NoResultsView>
      </CardsContainer>
      {!mobile && (
        <SidebarContainer>
          <Sidebar filters={filters} />
        </SidebarContainer>
      )}
    </NoResultsContainer>
  );
};
